import { useMemo, JSX } from "react";

import {
  GeneralInformation,
  PlanRates,
  HospitalIndemnityBenefits,
  HospitalIndemnityCoverage,
  HospitalIndemnityPlan,
} from "../../constants/aquafina";
import ContributionType from "../sections/contribution-type-section";
import { RangeIterator } from "../../utils";
import Rates from "../rates";
import CollapsableSection from "../sections/collapsable-section";
import { CoverageRow, NotProvidedRow } from "../table/rows";
import { CoverageCell } from "../table/cells";
import { ViewMode } from "../../constants/types";
import { VIEW_MODES } from "../../constants";
import PolicyGeneralInfo from "../sections/policy-general-info";
import ProposalGeneralInfo from "../sections/proposal-general-info";

type Props = {
  viewMode: ViewMode;
  coverage: HospitalIndemnityCoverage;
  displayV4Fields: boolean;
};

const Hospital = ({ coverage, viewMode, displayV4Fields }: Props) => {
  const state = useMemo(() => {
    const benefitDetails: HospitalIndemnityBenefits[] = [];
    const generalInfo: GeneralInformation[] = [];
    const contributionTypes = new Map<string, string[]>();
    const rates: PlanRates[] = [];
    const plans: HospitalIndemnityPlan[] = [];
    let inconsistentRateData = false;
    RangeIterator(coverage.plans.length, (index) => {
      const plan = coverage.plans[index];
      benefitDetails.push(plan.benefitDetails);
      generalInfo.push(plan.generalInformation);
      rates.push(plan.planRates);
      plans.push(plan);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });
    return {
      benefitDetails,
      generalInfo,
      contributionTypes,
      rates,
      plans,
      inconsistentRateData,
    };
  }, [coverage]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo data={state.generalInfo} />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo data={state.generalInfo} />
      )}
      <HospitalAdditionalPlanFeatures
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
        viewMode={viewMode}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
};

type HospitalBenefitBaseProps = {
  benefits: HospitalIndemnityBenefits[];
  displayV4Fields: boolean;
};

type HospitalAdditionalPlanFeaturesProps = HospitalBenefitBaseProps & {
  viewMode: ViewMode;
};

const HospitalAdditionalPlanFeatures = ({
  benefits,
  displayV4Fields,
  viewMode,
}: HospitalAdditionalPlanFeaturesProps): JSX.Element => (
  <CollapsableSection
    numberOfColumns={benefits.length}
    title="Additional Plan Features"
  >
    <HospitalAdditionalPlanFeaturesData
      benefits={benefits}
      displayV4Fields={displayV4Fields}
      viewMode={viewMode}
    />
  </CollapsableSection>
);

const HospitalAdditionalPlanFeaturesData = ({
  benefits,
  displayV4Fields,
  viewMode,
}: HospitalAdditionalPlanFeaturesProps): JSX.Element => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }
  return (
    <>
      <CoverageRow label="Hospital Admissions" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].hospitalAdmission} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="ICU Admissions">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].icuAdmission} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Hospital Confinement" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].hospitalConfinement}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="ICU Confinement">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].icuConfinement} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Pre-existing Condition" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].preExistingCondition}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Portability">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].portability} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Wellness Benefit" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].wellness} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields && viewMode !== VIEW_MODES.POLICY ? (
        <CoverageRow label="Annual Open Enrollment">
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell
              value={benefits[index].annualOpenEnrollment}
              key={index}
            />
          ))}
        </CoverageRow>
      ) : null}
    </>
  );
};

export default Hospital;
